import { useState, useRef, useCallback, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, X, Newspaper, FileText, FolderOpen, BarChart3 } from 'lucide-react';
import { createPortal } from 'react-dom';
import SimplizeOriginalLogo from './SimplizeOriginalLogo';

const SOURCE_TYPE_CONFIG = {
    web_search: { icon: Search },
    news: { icon: Newspaper },
    report: { icon: BarChart3 },
    document: { icon: FolderOpen },
    data: { icon: FileText },
};

const ICON_CLASS = 'text-gray-500';

function getTypeConfig(type) {
    return SOURCE_TYPE_CONFIG[type] || SOURCE_TYPE_CONFIG.data;
}

function SourceIcon({ group, className }) {
    if (group.label === 'Simplize') {
        return <SimplizeOriginalLogo iconOnly className={className} />;
    }
    const config = getTypeConfig(group.type);
    const Icon = config.icon;
    return <Icon className={`${className} ${ICON_CLASS}`} />;
}

function SourceGroup({ group, defaultExpanded = false }) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const sources = group.sources || [];
    const label = group.label || group.query || '';
    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
            >
                <SourceIcon group={group} className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" />
                <span className="text-[13px] text-gray-700 truncate flex-1">{label}</span>
                <span className="text-[11px] text-gray-400 flex-shrink-0">{sources.length}</span>
                {isExpanded
                    ? <ChevronUp className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    : <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                }
            </button>

            <div
                className="overflow-y-overlay transition-all duration-200 ease-in-out"
                style={{ maxHeight: isExpanded && sources.length > 0 ? '240px' : '0px', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.scrollbarColor = '#d1d5db transparent'; }}
                onMouseLeave={(e) => { e.currentTarget.style.scrollbarColor = 'transparent transparent'; }}
            >
                {sources.map((source, idx) => (
                    <a
                        key={idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-gray-600 hover:text-blue-600 hover:bg-gray-50 py-1.5 px-3 transition-colors"
                    >
                        <img
                            src={source.favicon || `https://www.google.com/s2/favicons?domain=${source.domain}&sz=16`}
                            alt=""
                            className="w-4 h-4 rounded-sm flex-shrink-0"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="truncate flex-1">{source.title || source.url}</span>
                        <span className="text-gray-400 flex-shrink-0 text-[11px]">{source.domain}</span>
                    </a>
                ))}
            </div>
        </div>
    );
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 400;

function SlidePanel({ sources, activeIndex, onClose }) {
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [isVisible, setIsVisible] = useState(false);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(DEFAULT_WIDTH);

    useEffect(() => {
        requestAnimationFrame(() => setIsVisible(true));
    }, []);

    const handleClose = useCallback(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    }, [onClose]);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        startX.current = e.clientX;
        startWidth.current = width;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            const delta = startX.current - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [width]);

    return createPortal(
        <div
            className="fixed top-0 right-0 bottom-0 z-40 bg-white border-l border-gray-200 shadow-lg flex flex-col transition-transform duration-300 ease-in-out"
            style={{
                width: `${width}px`,
                transform: isVisible ? 'translateX(0)' : `translateX(${width}px)`,
            }}
        >
            <div
                className="absolute top-0 left-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors"
                onMouseDown={handleMouseDown}
            />

            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Nguồn tham khảo</span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
                {sources.map((group, index) => (
                    <SourceGroup key={index} group={group} defaultExpanded />
                ))}
            </div>
        </div>,
        document.body,
    );
}

export default function WebSearchSourcesPanel({ sources }) {
    const [activeIndex, setActiveIndex] = useState(null);

    if (!sources || sources.length === 0) return null;

    return (
        <>
            <div className="border border-gray-200 rounded-2xl bg-white shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">Nguồn tham khảo</span>
                    </div>
                </div>
                <div className="px-3 pb-3 space-y-1 max-h-[40vh] overflow-y-auto">
                    {sources.map((group, index) => {
                        const label = group.label || group.query || '';
                        return (
                            <button
                                key={index}
                                onClick={() => setActiveIndex(index)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-50 transition-colors"
                            >
                                <SourceIcon group={group} className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" />
                                <span className="text-[13px] text-gray-700 truncate flex-1">{label}</span>
                                <span className="text-[11px] text-gray-400 flex-shrink-0">{(group.sources || []).length}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {activeIndex !== null && (
                <SlidePanel
                    sources={sources}
                    activeIndex={activeIndex}
                    onClose={() => setActiveIndex(null)}
                />
            )}
        </>
    );
}

export function WebSearchSourcesCompact({ sources }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    if (!sources || sources.length === 0) return null;

    return (
        <>
            <div className="xl:hidden mb-2 animate-slide-in-right">
                <div className="border border-gray-200 rounded-2xl bg-white shadow-sm overflow-hidden">
                    <button
                        onClick={() => setIsExpanded(prev => !prev)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    >
                        <span className="flex-1 text-sm text-gray-800 font-semibold truncate">Nguồn tham khảo</span>
                        {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            : <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        }
                    </button>
                    {isExpanded && (
                        <div className="px-3 pb-3 space-y-1">
                            {sources.map((group, index) => {
                                const label = group.label || group.query || '';
                                return (
                                    <button
                                        key={index}
                                        onClick={() => setActiveIndex(index)}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-gray-50 transition-colors"
                                    >
                                        <SourceIcon group={group} className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" />
                                        <span className="text-[13px] text-gray-700 truncate flex-1">{label}</span>
                                        <span className="text-[11px] text-gray-400 flex-shrink-0">{(group.sources || []).length}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {activeIndex !== null && (
                <SlidePanel
                    sources={sources}
                    activeIndex={activeIndex}
                    onClose={() => setActiveIndex(null)}
                />
            )}
        </>
    );
}
